<template>
  <aside
    v-if="hasCardContent"
    class="information-card d-flex flex-column ga-6 pa-5 rounded-30 bg-gray-0"
    data-qa="lpu_information_card"
  >
    <button
      v-if="mapSrc"
      type="button"
      class="information-card__map pa-0 rounded-30 border-sm"
      :data-popup-map="popupMapJson"
      data-qa="lpu_information_map"
      @click="handlePopupMapClick"
    >
      <img
        class="information-card__map-image"
        :src="mapSrc"
        :alt="mapAlt"
        loading="lazy"
      >
    </button>

    <div class="d-flex flex-column ga-5">
      <InformationField
        v-if="phone"
        :label="tStatic('Телефон')"
      >
        <a
          class="text-subtitle-1 text--text text-decoration-none"
          :href="`tel:${rawPhone}`"
        >
          {{ formattedPhone }}
        </a>
      </InformationField>

      <InformationField
        v-if="mainAddress"
        :label="tStatic('Адрес')"
      >
        <span>{{ tDynamic(mainAddress, 'address') }}</span>
        <a
          v-if="isStaff && !isMobile"
          class="adminka ml-1"
          :href="`/admin/doctors/lpuaddress/${mainAddress.id}/`"
          target="_blank"
          rel="noopener noreferrer"
        >
          A
        </a>
      </InformationField>

      <div
        v-if="metroItems.length"
        class="d-flex flex-column ga-1"
        data-qa="metro_list"
      >
        <div
          v-for="(metro, index) in metroItems"
          :key="`information-metro-${index}`"
          class="d-flex flex-wrap align-center text-body-2 text-secondary--text"
          data-qa="metro_item"
        >
          <span
            class="information-card__metro-color flex-shrink-0 mr-2"
            :style="{ backgroundColor: metro.metro.color }"
          />
          <span data-qa="metro_name">
            {{ tDynamic(metro.metro, 'name') }}
          </span>
          <span
            v-if="metro.walk_time != null"
            class="d-inline-flex align-center ml-2"
          >
            <span class="ui-icon-pedestrian ui-icon-fz-smaller mr-1" />
            <span data-qa="metro_distance">
              {{ metro.walk_time < 60 ? '1' : Math.floor(metro.walk_time / 60) }}
              {{ tStatic('мин') }}
            </span>
          </span>
        </div>
      </div>

      <div
        v-for="address in extraAddresses"
        :key="address.id"
        class="text-subtitle-1 text--text"
      >
        <span>{{ tDynamic(address, 'address') }}</span>
        <a
          v-if="isStaff && !isMobile"
          class="adminka ml-1"
          :href="`/admin/doctors/lpuaddress/${address.id}/`"
          target="_blank"
          rel="noopener noreferrer"
        >
          A
        </a>
      </div>

      <InformationField
        v-if="networkName && networkHref"
        :label="tStatic('Входит в состав сети')"
      >
        <a
          class="text-subtitle-1 primary--text text-decoration-none"
          :href="networkHref"
        >
          {{ networkName }}
        </a>
      </InformationField>

      <InformationField
        v-if="officialName"
        :label="tStatic('Официальное название')"
      >
        {{ officialName }}
      </InformationField>

      <InformationField
        v-if="directorName"
        :label="tStatic('Руководитель')"
      >
        {{ directorName }}
      </InformationField>

      <InformationActions
        v-if="siteUrl || licenseItems.length"
        :site-url="siteUrl"
        :license-items="licenseItems"
      />
    </div>
  </aside>
</template>

<script lang="ts" setup>
import {
  computed,
} from 'vue'
import type { CommonContextStore } from 'src/types/context'
import useIsMobile from 'src/composables/useIsMobile'
import useContextStore from 'src/store/useContextStore'
import useURLStore from 'src/store/useURLStore'
import formatPhone from 'src/functions/formatPhone'
import getLpuPhone from 'src/utils/getLpuPhone'
import tDynamic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tDynamic'
import tStatic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tStatic'
import getYandexStaticMapUrl from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuInformation/functions/getYandexStaticMapUrl'
import openPopupMap from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/openPopupMap'
import type { CoreImageLightboxItem } from 'src/core/CoreImageLightbox/types'
import type { Lpu } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_types'
import InformationActions from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuInformation/components/InformationActions.vue'
import InformationField from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuInformation/components/InformationField.vue'

const PHARMACY_LPU_TYPE_ID = 51
const METRO_LIMIT = 3

const props = withDefaults(defineProps<{
  lpu: Lpu
  licenseItems?: CoreImageLightboxItem[]
}>(), {
  licenseItems: () => [],
})

const isMobile = useIsMobile()
const { context, urlLang }: CommonContextStore = useContextStore()
const { reverseFull, reverseFullL10N } = useURLStore()

const isStaff = computed(() => Boolean(context.request.user.is_staff))
const isPharmacy = computed(() => props.lpu.lputype.id === PHARMACY_LPU_TYPE_ID)
const mainAddress = computed(() => {
  if (isPharmacy.value || !props.lpu.addr?.length) {
    return undefined
  }

  return props.lpu.addr.find(address => address.is_main) || props.lpu.addr[0]
})

const mapSrc = computed(() => {
  if (!mainAddress.value) {
    return ''
  }

  return getYandexStaticMapUrl(mainAddress.value.lat, mainAddress.value.lon)
})

const mapAlt = computed(() => {
  const lpuName = tDynamic(props.lpu, 'name')
  const address = mainAddress.value
    ? tDynamic(mainAddress.value, 'address')
    : ''

  if (!address) {
    return `${lpuName} — ${tStatic('карта')}`
  }

  return `${lpuName}, ${address} — ${tStatic('карта')}`
})

const phone = computed(() => getLpuPhone({
  lpu: props.lpu,
  lpuAddress: props.lpu.addr?.[0],
}) || '')

const formattedPhone = computed(() => (
  phone.value
    ? formatPhone({ phone: phone.value, country: context.request.country })
    : ''
))

const rawPhone = computed(() => (
  phone.value
    ? formatPhone({ phone: phone.value, country: context.request.country, isRaw: true })
    : ''
))

const extraAddresses = computed(() => {
  if (!props.lpu.lputype.is_private && !props.lpu.paid && !isStaff.value) {
    return []
  }

  return (props.lpu.addr ?? []).filter(address => !address.is_main)
})

const metroItems = computed(() => {
  if (props.lpu.closed) {
    return []
  }

  return (props.lpu.lpumetro_set?.all ?? []).slice(0, METRO_LIMIT)
})

const officialName = computed(() => tDynamic(props.lpu, 'name_full'))
const directorName = computed(() => tDynamic(props.lpu, 'fio'))
const networkName = computed(() => (
  props.lpu.lpuset
    ? tDynamic(props.lpu.lpuset, 'name')
    : ''
))
const networkHref = computed(() => {
  if (!props.lpu.lpuset) {
    return ''
  }

  return reverseFull({
    name: 'lpuset',
    args: [props.lpu.lpuset.town.translit, props.lpu.lpuset.translit],
  })
})

const siteUrl = computed(() => (
  props.lpu.site && !props.lpu.hide_site
    ? props.lpu.site
    : ''
))

const popupMapJson = computed(() => {
  if (!mainAddress.value) {
    return ''
  }

  return JSON.stringify([
    {
      coords: [
        mainAddress.value.lat,
        mainAddress.value.lon,
      ],
      phone: formattedPhone.value,
      name: props.lpu.name,
      address: mainAddress.value.is_main ? mainAddress.value.address : '',
      id: props.lpu.id,
      lpuHref: reverseFullL10N({
        name: 'lpu',
        args: {
          language: urlLang,
          town_translit: props.lpu.town.translit,
          lpu_translit: props.lpu.translit,
        },
      }),
    },
  ])
})

function handlePopupMapClick() {
  if (!popupMapJson.value) {
    return
  }

  openPopupMap(JSON.parse(popupMapJson.value))
}

const hasCardContent = computed(() => (
  Boolean(mapSrc.value)
  || Boolean(phone.value)
  || Boolean(mainAddress.value)
  || extraAddresses.value.length > 0
  || metroItems.value.length > 0
  || Boolean(networkName.value)
  || Boolean(officialName.value)
  || Boolean(directorName.value)
  || Boolean(siteUrl.value)
  || props.licenseItems.length > 0
))
</script>

<style scoped lang="scss">
@import 'www/themes/doctors/common/variables';

.information-card {
  box-sizing: border-box;
  box-shadow: 0 2px 24px 0 rgba(9, 42, 72, 0.08),
    0 0 2px 0 rgba(21, 40, 92, 0.06),
    0 1px 2px 0 rgba(5, 23, 40, 0.08);

  &__map {
    display: block;
    width: 100%;
    height: 237px;
    overflow: hidden;
    background: transparent;
    cursor: pointer;
  }

  &__map-image {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    pointer-events: none;
  }

  &__metro-color {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: $border-radius-circle;
  }
}
</style>
